import React, { useMemo } from 'react';
import useFormatMessage from '@broadleaf/admin-components/dist/common/hooks/useFormatMessage';
import { useFormatNumber } from '@broadleaf/admin-components/dist/common';
import useContextParams from '@broadleaf/admin-components/dist/oms/components/FulfillmentView/hooks/useContextParams';
import useEventCallback from '@broadleaf/admin-components/dist/common/hooks/useEventCallback';
import { request } from '@broadleaf/admin-components/dist/metadata/utils/request';
import {
  clearFormikErrors,
  setFormikErrors
} from '@broadleaf/admin-components/dist/form/utils/RequestErrorHelpers';
import { Formik } from 'formik';
import * as yup from 'yup';
import { UserAccess } from '@broadleaf/admin-components/dist/authentication';
import { SelectField } from '@src/custom/common/form/SelectField';
import { InputField } from '@src/custom/common/form/InputField';
import { SubmitButton } from '@src/custom/common/form/SubmitButton';
import { IMetadata } from '@broadleaf/admin-components/dist/types/metadata';
import {
  IConsolidatedOrder,
  IInvoiceSummary
} from '@src/oms/utils/RmFulfillmentUtils';
import messages from './InvoiceDetailsModal.messages';
import { getApplyInvoicePaymentEndpoint } from '@src/oms/InvoicesView/utils/InvoicesOperationUtils';
import Link from '@broadleaf/admin-components/dist/common/elements/Link';

type AddInvoicePaymentFormProps = {
  metadata: IMetadata;
  refresh: () => Promise<void>;
  onClose: () => void;
  consolidatedOrder: IConsolidatedOrder;
  onUpdateComplete: () => Promise<void>;
};
export const AddInvoicePaymentForm: React.FC<AddInvoicePaymentFormProps> = props => {
  const { metadata, refresh, consolidatedOrder, onUpdateComplete } = props;
  const invoice = consolidatedOrder.invoiceSummary;
  const invoiceBalanceAmount = useMemo(() => {
    return invoice.invoiceBalance.amount;
  }, [invoice]);
  const availableRevMedCredit =
    consolidatedOrder.revmedCreditAccount?.balance?.amount;
  const maxRevMedCreditAmount = useMemo(() => {
    return availableRevMedCredit &&
      availableRevMedCredit > invoice.invoiceBalance.amount
      ? invoice.invoiceBalance.amount
      : availableRevMedCredit || 0;
  }, [availableRevMedCredit, invoice]);

  const contextParams = useContextParams(metadata);
  const initialValues = useMemo(() => {
    return {
      invoiceId: invoice.invoiceId,
      amount: {
        amount: invoiceBalanceAmount,
        currency: invoice.invoiceBalance.currency
      },
      reasonCode: '',
      reasonNote: ''
    };
  }, [invoiceBalanceAmount, invoice]);
  const validationSchema = useMemo(() => {
    return yup.object().shape({
      invoiceId: yup.string(),
      amount: yup.object().shape({
        amount: yup
          .number()
          .required()
          .min(invoiceBalanceAmount < 0 ? invoiceBalanceAmount : 0)
          .test('amount', 'Amount above max value', function (value) {
            const { reasonCode } = this.options.context;
            if (reasonCode === 'MANUAL_REVMED_CREDIT') {
              return value <= maxRevMedCreditAmount;
            } else {
              return value <= invoiceBalanceAmount;
            }
          }),
        currency: yup.string().required()
      }),
      reasonCode: yup.string().required(),
      reasonNote: yup.string().required()
    });
  }, [invoiceBalanceAmount, maxRevMedCreditAmount]);
  const handleSubmit = useEventCallback(
    async (values, formik) => {
      formik.setSubmitting(true);
      try {
        const endpoint = getApplyInvoicePaymentEndpoint(values.invoiceId);

        const { data } = await request(
          { method: 'post', data: values, ...endpoint },
          contextParams
        );

        formik.setSubmitting(false);
        clearFormikErrors(formik);

        await refresh();
        // don't wait for update complete
        onUpdateComplete();
      } catch (error) {
        setFormikErrors(error, formik);
        formik.setSubmitting(false);
      } finally {
        formik.setSubmitting(false);
      }
    },
    [contextParams, refresh, onUpdateComplete]
  );
  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema}
      >
        {formik => (
          <AddInvoicePaymentSection
            formik={formik}
            consolidatedOrder={consolidatedOrder}
            invoice={invoice}
            invoiceBalanceAmount={invoiceBalanceAmount}
            maxRevMedCreditAmount={maxRevMedCreditAmount}
          />
        )}
      </Formik>
    </>
  );
};

export type AddSellerPaymentSectionProps = {
  formik: any;
  consolidatedOrder: IConsolidatedOrder;
  invoice: IInvoiceSummary;
  invoiceBalanceAmount: number;
  maxRevMedCreditAmount: number;
};
export const AddInvoicePaymentSection: React.FC<AddSellerPaymentSectionProps> = ({
  formik,
  consolidatedOrder,
  invoice,
  invoiceBalanceAmount,
  maxRevMedCreditAmount
}) => {
  const checkAccess = UserAccess.useCheckAccess();
  const formatNumber = useFormatNumber();
  const formatMessage = useFormatMessage();
  const hasAccess = checkAccess('INVOICE', 'UPDATE');

  const isDisabled = useMemo(() => {
    return (
      !hasAccess ||
      invoiceBalanceAmount == 0 ||
      // we dont allow adding seller payments once we've passed the Seller Payout Pending status or it has been cancelled
      ![
        'ORDER_PROCESSING',
        'CREDIT_PENDING',
        'HOLD_FOR_REVIEW',
        'REQUIRES_PO',
        'READY_TO_SUBMIT',
        'OPEN_REVMED_INVOICE'
      ].includes(invoice.invoiceStatus)
    );
  }, [hasAccess, invoiceBalanceAmount, invoice]);

  const paymentMethods = [
    {
      label: formatMessage(messages.paymentMethods.revmedCredit),
      value: 'MANUAL_REVMED_CREDIT'
    },
    {
      label: formatMessage(messages.paymentMethods.invoicePaid),
      value: 'INVOICE_PAID'
    },
    {
      label: formatMessage(messages.paymentMethods.wire),
      value: 'WIRE_TRANSFER'
    },
    {
      label: formatMessage(messages.paymentMethods.ach),
      value: 'ACH'
    },
    {
      label: formatMessage(messages.paymentMethods.check),
      value: 'CHECK'
    },
    {
      label: formatMessage(messages.paymentMethods.other),
      value: 'OTHER'
    }
  ];

  return (
    <>
      <div className="tw-flex tw-flex-1 tw-flex-col tw-border-b tw-border-gray-500">
        <div className="tw-flex tw-flex-1">
          <div className="tw-flex-1">
            <SelectField
              formik={formik}
              label="Method"
              required
              name="reasonCode"
              placeholder="Choose a Payment Method"
              options={paymentMethods}
              onChange={option => {
                if (option.value !== 'OTHER') {
                  formik.setFieldValue('reasonNote', option.label);
                } else {
                  formik.setFieldValue('reasonNote', '');
                }
                if (option.value === 'MANUAL_REVMED_CREDIT') {
                  formik.setFieldValue('amount.amount', maxRevMedCreditAmount);
                }
              }}
              isDisabled={isDisabled}
            />
          </div>
          <div className="tw-flex-1">
            <InputField
              fullWidth={true}
              className="tw-w-full"
              formik={formik}
              required
              name={`reasonNote`}
              isDisabled={isDisabled || formik.values.reasonCode !== 'OTHER'}
              label="Description"
            />
          </div>
          <div className="tw-flex-1">
            <InputField
              fullWidth={true}
              formik={formik}
              required
              name={`amount.amount`}
              min={invoiceBalanceAmount < 0 ? invoiceBalanceAmount : 0}
              max={invoiceBalanceAmount < 0 ? 0 : invoiceBalanceAmount}
              step=".01"
              type="number"
              isDisabled={isDisabled}
              label="Payment Amount"
            />
          </div>
        </div>
        <div className="tw-flex tw-flex-1">
          <span className="tw-text-xs tw-text-gray-500">
            Available RevMed Credit:{' '}
            {formatNumber(
              consolidatedOrder.revmedCreditAccount?.balance?.amount || 0,
              {
                style: 'currency',
                currency:
                  consolidatedOrder.revmedCreditAccount?.balance?.currency ||
                  'USD'
              }
            )}
          </span>
          {consolidatedOrder.revmedCreditAccount && (
            <Link
              to={`/customer-accounts/${consolidatedOrder.revmedCreditAccount.owningUserRef}/store-credit-account/REVMED_CREDIT`}
              className="tw-ml-2 tw-inline-block tw-text-xs tw-text-link-600 hover:tw-text-link-700"
            >
              View Details
            </Link>
          )}
        </div>

        <div className="tw-flex tw-flex-1 tw-flex-row">
          <div className="tw-flex-1"></div>
          <div className="tw-flex tw-flex-1 tw-flex-row">
            <div className="tw-flex-0 tw-ml-auto tw-h-16">
              <SubmitButton
                formik={formik}
                isSubmitting={formik.isSubmitting}
                isDisabled={isDisabled}
                submitLabel="Apply Payment"
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
